import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { normalizeCurrency } from '@/lib/currency';
import { propertyTypeMeta, type HebergementProperty } from '@/lib/hebergement';

interface HebergementsTagProps {
  limit?: number;
}

interface MediaRow {
  property_id: number | null;
  url: string;
  type: string;
  is_cover: boolean;
  sort_order: number;
}

/**
 * Balise {{hebergements}} — liste les biens d'hébergement PUBLIÉS du marchand
 * sur son site public. Les données proviennent uniquement des tables
 * `hebergement_properties` / `hebergement_media` scopées par `merchant_id`.
 * Aucune donnée fictive : s'il n'y a rien de publié, le bloc ne s'affiche pas.
 */
export default function HebergementsTag({ limit }: HebergementsTagProps) {
  const { tenant, isTenant } = useTenant();
  const merchantId = tenant?.id ?? null;
  const currency = normalizeCurrency(tenant?.monaie || 'MAD');

  const [properties, setProperties] = useState<HebergementProperty[]>([]);
  const [covers, setCovers] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isTenant || !merchantId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    (async () => {
      try {
        const [propRes, mediaRes] = await Promise.all([
          supabase
            .from('hebergement_properties')
            .select('*')
            .eq('merchant_id', merchantId)
            .eq('published', true)
            .eq('status', 'active')
            .order('created_at', { ascending: false }),
          supabase
            .from('hebergement_media')
            .select('property_id, url, type, is_cover, sort_order')
            .eq('merchant_id', merchantId)
            .order('sort_order', { ascending: true }),
        ]);
        if (propRes.error) throw propRes.error;
        if (cancelled) return;

        const list = (propRes.data || []) as HebergementProperty[];
        const selected = limit && limit > 0 ? list.slice(0, limit) : list;
        setProperties(selected);

        const map: Record<number, string> = {};
        for (const m of (mediaRes.data || []) as MediaRow[]) {
          if (m.property_id == null || m.type === 'video') continue;
          if (!map[m.property_id] || m.is_cover) map[m.property_id] = m.url;
        }
        setCovers(map);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isTenant, merchantId, limit]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-14">
        <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-foreground-500 text-sm">
        <i className="ri-error-warning-line"></i>
        Impossible de charger les hébergements pour le moment.
      </div>
    );
  }

  if (properties.length === 0) return null;

  return (
    <section className="w-full px-4 md:px-6 py-10">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-50 text-secondary-700 text-xs font-medium mb-3">
            <i className="ri-hotel-line"></i>Hébergements
          </span>
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">Nos hébergements</h2>
          <p className="text-sm text-foreground-500 mt-2 max-w-xl mx-auto">
            Villas, appartements, riads et chambres — réservez directement, disponibilités en temps réel.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
          {properties.map((p) => {
            const meta = propertyTypeMeta(p.type);
            const cover = covers[p.id] || p.cover_image || '';
            const price = Number(p.price) || 0;
            return (
              <Link
                key={p.id}
                to={`/hebergement/${p.id}`}
                className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-background-300/60 transition-all duration-200 group no-underline flex flex-col"
              >
                <div className="relative aspect-[16/10] bg-background-100 overflow-hidden">
                  {cover ? (
                    <img
                      src={cover}
                      alt={p.name}
                      title={`${p.name} — hébergement`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-foreground-300">
                      <i className="ri-image-line text-4xl"></i>
                    </div>
                  )}
                  <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full text-[11px] font-medium bg-background-50/90 text-foreground-700 backdrop-blur-sm flex items-center gap-1">
                    <i className={meta.icon}></i>{meta.label}
                  </span>
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">{p.name}</h3>
                  <p className="text-xs text-foreground-500 mt-1 truncate">
                    <i className="ri-map-pin-line mr-1"></i>
                    {[p.city, p.country].filter(Boolean).join(', ') || 'Localisation non renseignée'}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3 text-[11px] text-foreground-600">
                    <span className="px-2 py-0.5 rounded-full bg-background-100 flex items-center gap-1"><i className="ri-user-line"></i>{p.capacity}</span>
                    <span className="px-2 py-0.5 rounded-full bg-background-100 flex items-center gap-1"><i className="ri-hotel-bed-line"></i>{p.bed_count}</span>
                  </div>
                  <div className="mt-auto pt-4 flex items-end justify-between">
                    <div className="text-base font-bold font-heading text-primary-600">
                      {price.toLocaleString()} <span className="text-xs font-normal text-foreground-400">{currency} / nuit</span>
                    </div>
                    <span className="text-[10px] text-foreground-400">Voir &amp; réserver</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}